import { alpha, Card, CardContent, Checkbox, FormControlLabel, Grid, Paper, Theme, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { InventoryVersion } from '../../../../hooks/useInventory';
import useSextforceBigBrotherRestrictedWords from '../../../../hooks/useSextforceBigBrotherRestrictedWords';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import useSubscriberMedia from '../../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditional from '../../../../hooks/useSubscriberVaultMediasAdditional';
import {
    createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails,
    createEmptyOnlyFansSubscriberMessageUsersTtsSettings,
    OnlyFansListIdAndName,
    OnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersTtsSettings,
} from '../../../../types/messageUsersAutoSendWelcomeMessage';
import { getRestrictedWordsInMessage } from '../../../../utils/messageUsers';
import OnlyFansListsMultiSelector from '../../../common/OnlyFansListsMultiSelector';
import OnlyFansListsSelector from '../../../common/OnlyFansListsSelector';
import StyledCard from '../../../common/StyledCard';
import QuickSelectInventory from '../../../inventory/QuickSelectInventory';
import TtsMessageSettingsCard from '../../tts/TtsMessageSettingsCard';
import MessageUsersFormAddMedias from '../sendMessage/MessageUsersFormAddMedias';
import MessageUsersFormEditor from '../sendMessage/MessageUsersFormEditor';
import MessageUsersPriceLock from '../sendMessage/MessageUsersPriceLock';
import MessageUsersTagCreators from '../sendMessage/MessageUsersTagCreators';
import AutoSendWelcomeMessageCurrentSettings from './AutoSendWelcomeMessageCurrentSettings';
import { formReducer, FormState, initialFormState } from './formReducer';
import { validateForm, ValidationDependencies } from './formValidation';

// Type for thumbnails
interface Thumbnail {
    thumbId: number;
    blob: Blob | null;
}

/**
 * Safely initialize form state from messageDetails, handling null/undefined values
 */
const initializeFormState = (messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails): FormState => {
    return {
        ...initialFormState,
        includeMedia: (messageDetails.mediaFiles?.length ?? 0) > 0,
        medias: messageDetails.mediaFiles || [],
        mediasPreviews: messageDetails.mediaFilesPreviews || [],
        tagCreators: (messageDetails.taggedCreators?.length ?? 0) > 0,
        taggedCreators: messageDetails.taggedCreators || [],
        taggedReleaseForms: messageDetails.taggedReleaseForms || [],
        lockMessage: messageDetails.lockMessagePrice !== null && messageDetails.lockMessagePrice !== undefined,
        lockedText: Boolean(messageDetails.lockedText),
        usernameReplacement: messageDetails.replaceUsername || '',
        excludeUserInLists: Boolean(messageDetails.excludeLists && messageDetails.excludeLists.length > 0),
        excludeLists: messageDetails.excludeLists || [],
        addToList: messageDetails.addToList !== null && messageDetails.addToList !== undefined,
        addToListIdAndName: messageDetails.addToList || undefined,
        ttsSettings: messageDetails.tts || createEmptyOnlyFansSubscriberMessageUsersTtsSettings(false),
        templateText: messageDetails.message || '',
    };
};

type Props = {
    title: string;
    description: string;
    subscriber: OnlyFansSubscriber;
    initialValues: OnlyFansSubscriberMessageUsersAutoMessageDetails | null;
    messageDetails: OnlyFansSubscriberMessageUsersAutoMessageDetails;
    setMessageDetails: React.Dispatch<React.SetStateAction<OnlyFansSubscriberMessageUsersAutoMessageDetails | null>>;
    vaultAlbumName: string | undefined;
    setVaultAlbumName: (vaultAlbumName: string | undefined) => void;
    saveTemplate: boolean;
    setSaveTemplate: (saveTemplate: boolean) => void;
    saveTemplateName: string;
    setSaveTemplateName: (saveTemplateName: string) => void;
    setIsFormValid: React.Dispatch<React.SetStateAction<boolean>>;
    showAddToList?: boolean;
    showTts?: boolean;
    showInitialCheckBox?: boolean;
};

const AutoSendWelcomeMessageDetailsForm = (props: Props) => {
    const {
        title,
        description,
        subscriber,
        initialValues,
        messageDetails,
        setMessageDetails,
        vaultAlbumName,
        setVaultAlbumName,
        saveTemplate,
        setSaveTemplate,
        saveTemplateName,
        setSaveTemplateName,
        setIsFormValid,
        showAddToList = false,
        showTts = false,
        showInitialCheckBox = true,
    } = props;

    const theme: Theme = useTheme();
    const { getThumbnail: getSubscriberThumbnail, getAudio: getSubscriberAudio } = useSubscriberMedia();
    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditional();

    // Consolidated state using useReducer
    const [formState, dispatch] = useReducer(formReducer, initializeFormState(messageDetails));

    // Separate state for items that don't fit the reducer pattern well
    const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const [selectedInventory, setSelectedInventory] = useState<InventoryVersion>();

    // Helper function to update message details
    const updateMessageDetails = useCallback(
        (updates: Partial<OnlyFansSubscriberMessageUsersAutoMessageDetails>) => {
            setMessageDetails(prevState => ({
                ...(prevState || createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails()),
                ...updates,
            }));
        },
        [setMessageDetails],
    );

    // Handle inventory selection with proper error handling
    useEffect(() => {
        const switchInventory = async () => {
            if (!selectedInventory) return;

            try {
                // Reset values
                setMessageDetails(initialValues);
                dispatch({ type: 'RESET_FORM' });

                // Input new ones
                const updates: Partial<OnlyFansSubscriberMessageUsersAutoMessageDetails> = {
                    message: selectedInventory.text,
                };

                dispatch({ type: 'SET_TEMPLATE_TEXT', payload: selectedInventory.text });

                if (Number(selectedInventory.price) > 0) {
                    dispatch({ type: 'SET_LOCK_MESSAGE', payload: true });
                    updates.lockMessagePrice = Number(selectedInventory.price);
                }

                if (selectedInventory.creatorTags && selectedInventory.creatorTags.length > 0) {
                    const creators = selectedInventory.creatorTags.map(item => ({
                        id: item.creatorOnlyfansId,
                        name: item.creatorName,
                    }));
                    dispatch({ type: 'SET_TAG_CREATORS', payload: true });
                    dispatch({ type: 'SET_TAGGED_CREATORS', payload: creators });
                }

                const hasMedia = (selectedInventory.unlockedMedias?.length ?? 0) > 0 || (selectedInventory.lockedMedias?.length ?? 0) > 0;

                if (hasMedia) {
                    dispatch({ type: 'SET_INCLUDE_MEDIA', payload: true });

                    const mediaIds = [
                        ...(selectedInventory.lockedMedias?.map(item => item.mediaId) ?? []),
                        ...(selectedInventory.unlockedMedias?.map(item => item.mediaId) ?? []),
                    ];

                    const grabMedias = await getManyVaultMediaFromDatabase(mediaIds);
                    dispatch({ type: 'SET_MEDIAS', payload: grabMedias });
                    updates.mediaFiles = grabMedias;

                    if ((selectedInventory.unlockedMedias?.length ?? 0) > 0) {
                        const previews = selectedInventory.unlockedMedias?.map(item => item.mediaId) ?? [];
                        dispatch({ type: 'SET_MEDIAS_PREVIEWS', payload: previews });
                        updates.mediaFilesPreviews = previews;
                    }
                }

                updateMessageDetails(updates);
            } catch (error) {
                console.error('Failed to switch inventory:', error);
                // TODO: Show user-friendly error message (e.g., using a snackbar)
            }
        };

        switchInventory();
    }, [selectedInventory, initialValues, getManyVaultMediaFromDatabase, setMessageDetails, updateMessageDetails]);

    // Check for restricted words
    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (text && !isLoadingRestrictedWords && restrictedWords && subscriber) {
                const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);
                setMessageHasRestrictedWords(restrictedWordsFound.length > 0);
                setMessageFoundRestrictedWords(restrictedWordsFound);
            } else {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
            }
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );

    // Sync form state with messageDetails
    useEffect(() => {
        updateMessageDetails({ mediaFiles: formState.medias });
    }, [formState.medias, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({ taggedCreators: formState.taggedCreators });
    }, [formState.taggedCreators, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({ taggedReleaseForms: formState.taggedReleaseForms });
    }, [formState.taggedReleaseForms, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({ mediaFilesPreviews: formState.mediasPreviews });
    }, [formState.mediasPreviews, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({ replaceUsername: formState.usernameReplacement });
    }, [formState.usernameReplacement, updateMessageDetails]);

    useEffect(() => {
        if (!formState.lockMessage) {
            updateMessageDetails({ lockMessagePrice: null });
        }
    }, [formState.lockMessage, updateMessageDetails]);

    useEffect(() => {
        if (!formState.lockedText) {
            updateMessageDetails({ lockedText: false });
        }
    }, [formState.lockedText, updateMessageDetails]);

    useEffect(() => {
        if (!formState.includeMedia) {
            dispatch({ type: 'SET_MEDIAS', payload: [] });
            dispatch({ type: 'SET_MEDIAS_PREVIEWS', payload: [] });
        }
    }, [formState.includeMedia]);

    useEffect(() => {
        if (!formState.excludeUserInLists) {
            dispatch({ type: 'SET_EXCLUDE_LISTS', payload: [] });
        }
    }, [formState.excludeUserInLists]);

    useEffect(() => {
        updateMessageDetails({
            excludeLists: formState.excludeUserInLists ? formState.excludeLists : [],
        });
    }, [formState.excludeLists, formState.excludeUserInLists, updateMessageDetails]);

    useEffect(() => {
        if (!formState.addToList) {
            updateMessageDetails({ addToList: null });
            dispatch({ type: 'SET_ADD_TO_LIST_ID_AND_NAME', payload: null });
        }
    }, [formState.addToList, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({
            addToList: formState.addToListIdAndName || null,
        });
    }, [formState.addToListIdAndName, updateMessageDetails]);

    useEffect(() => {
        updateMessageDetails({ tts: formState.ttsSettings });
    }, [formState.ttsSettings, updateMessageDetails]);

    // Form validation
    useEffect(() => {
        const validationDeps: ValidationDependencies = {
            messageDetails,
            includeMedia: formState.includeMedia,
            medias: formState.medias,
            lockMessage: formState.lockMessage,
            tagCreators: formState.tagCreators,
            taggedCreators: formState.taggedCreators,
            taggedReleaseForms: formState.taggedReleaseForms,
            addToList: formState.addToList,
            addToListIdAndName: formState.addToListIdAndName,
            excludeUserInLists: formState.excludeUserInLists,
            excludeLists: formState.excludeLists,
            ttsSettings: formState.ttsSettings,
            subscriber,
        };

        const isValid = validateForm(validationDeps);
        setIsFormValid(isValid);
    }, [
        messageDetails,
        formState.includeMedia,
        formState.lockMessage,
        formState.medias,
        formState.tagCreators,
        formState.taggedCreators,
        formState.taggedReleaseForms,
        formState.addToList,
        formState.addToListIdAndName,
        formState.excludeUserInLists,
        formState.excludeLists,
        formState.ttsSettings,
        subscriber,
        setIsFormValid,
    ]);

    // Memoized thumbnail getter
    const getThumbnail = useCallback(
        (thumbId: number, thumbUrl: string) => {
            getSubscriberThumbnail(subscriber, thumbId, thumbUrl)
                ?.then(blob => {
                    setThumbnails(prevState => [...prevState, { thumbId, blob: blob || null }]);
                })
                .catch(error => {
                    console.error('Failed to fetch thumbnail:', error);
                    setThumbnails(prevState => [...prevState, { thumbId, blob: null }]);
                });
        },
        [subscriber, getSubscriberThumbnail],
    );

    const getAudio = useCallback((url: string) => getSubscriberAudio(subscriber, url), [subscriber, getSubscriberAudio]);

    // Memoized child components
    const FormContainer = useMemo(() => (showInitialCheckBox ? StyledCard : Grid), [showInitialCheckBox]);

    // Handler for TTS settings that accepts both value and function forms
    const handleSetTtsSettings = useCallback(
        (settingsOrUpdater: React.SetStateAction<OnlyFansSubscriberMessageUsersTtsSettings>) => {
            if (typeof settingsOrUpdater === 'function') {
                // If it's a function, call it with current state and dispatch the result
                const newSettings = settingsOrUpdater(formState.ttsSettings);
                dispatch({ type: 'SET_TTS_SETTINGS', payload: newSettings });
            } else {
                // If it's a value, dispatch it directly
                dispatch({ type: 'SET_TTS_SETTINGS', payload: settingsOrUpdater });
            }
        },
        [formState.ttsSettings],
    );

    // Helper to handle SetStateAction pattern for medias
    const handleSetMedias = useCallback(
        (mediasOrUpdater: React.SetStateAction<OnlyFansVaultMedias.RestructuredResponseItem[]>) => {
            if (typeof mediasOrUpdater === 'function') {
                const newMedias = mediasOrUpdater(formState.medias);
                dispatch({ type: 'SET_MEDIAS', payload: newMedias });
            } else {
                dispatch({ type: 'SET_MEDIAS', payload: mediasOrUpdater });
            }
        },
        [formState.medias],
    );

    // Helper to handle SetStateAction pattern for mediasPreviews
    const handleSetMediasPreviews = useCallback(
        (previewsOrUpdater: React.SetStateAction<number[]>) => {
            if (typeof previewsOrUpdater === 'function') {
                const newPreviews = previewsOrUpdater(formState.mediasPreviews);
                dispatch({ type: 'SET_MEDIAS_PREVIEWS', payload: newPreviews });
            } else {
                dispatch({ type: 'SET_MEDIAS_PREVIEWS', payload: previewsOrUpdater });
            }
        },
        [formState.mediasPreviews],
    );

    // Helper to handle SetStateAction pattern for taggedCreators
    const handleSetTaggedCreators = useCallback(
        (creatorsOrUpdater: React.SetStateAction<{ id: number; name: string }[]>) => {
            if (typeof creatorsOrUpdater === 'function') {
                const newCreators = creatorsOrUpdater(formState.taggedCreators);
                dispatch({ type: 'SET_TAGGED_CREATORS', payload: newCreators });
            } else {
                dispatch({ type: 'SET_TAGGED_CREATORS', payload: creatorsOrUpdater });
            }
        },
        [formState.taggedCreators],
    );

    // Helper to handle SetStateAction pattern for taggedReleaseForms
    const handleSetTaggedReleaseForms = useCallback(
        (formsOrUpdater: React.SetStateAction<{ id: number; name: string }[]>) => {
            if (typeof formsOrUpdater === 'function') {
                const newForms = formsOrUpdater(formState.taggedReleaseForms);
                dispatch({ type: 'SET_TAGGED_RELEASE_FORMS', payload: newForms });
            } else {
                dispatch({ type: 'SET_TAGGED_RELEASE_FORMS', payload: formsOrUpdater });
            }
        },
        [formState.taggedReleaseForms],
    );

    // Helper to handle SetStateAction pattern for excludeLists
    const handleSetExcludeLists = useCallback(
        (listsOrUpdater: React.SetStateAction<OnlyFansListIdAndName[]>) => {
            if (typeof listsOrUpdater === 'function') {
                const newLists = listsOrUpdater(formState.excludeLists);
                dispatch({ type: 'SET_EXCLUDE_LISTS', payload: newLists });
            } else {
                dispatch({ type: 'SET_EXCLUDE_LISTS', payload: listsOrUpdater });
            }
        },
        [formState.excludeLists],
    );

    // Helper to handle SetStateAction pattern for addToListIdAndName
    const handleSetAddToListIdAndName = useCallback(
        (listOrUpdater: React.SetStateAction<OnlyFansListIdAndName | undefined | null>) => {
            if (typeof listOrUpdater === 'function') {
                const newList = listOrUpdater(formState.addToListIdAndName);
                dispatch({ type: 'SET_ADD_TO_LIST_ID_AND_NAME', payload: newList });
            } else {
                dispatch({ type: 'SET_ADD_TO_LIST_ID_AND_NAME', payload: listOrUpdater });
            }
        },
        [formState.addToListIdAndName],
    );

    const memoizedTtsCard = useMemo(
        () =>
            showTts ? (
                <TtsMessageSettingsCard
                    subscriber={subscriber}
                    ttsSettings={formState.ttsSettings}
                    setTtsSettings={handleSetTtsSettings}
                    disabled={!messageDetails?.active}
                />
            ) : null,
        [showTts, subscriber, formState.ttsSettings, messageDetails?.active, handleSetTtsSettings],
    );

    const memoizedQuickSelectInventory = useMemo(
        () =>
            messageDetails?.active ? (
                <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                    <Grid item xs={12}>
                        <Card variant="elevation">
                            <CardContent>
                                <QuickSelectInventory setSelectedInventoryVersion={setSelectedInventory} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : null,
        [messageDetails?.active],
    );

    return (
        <FormContainer>
            {initialValues && (
                <Grid container spacing={1} flexGrow={0} alignItems="center">
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="medium"
                                    checked={messageDetails?.active ?? false}
                                    onChange={(e, checked) => {
                                        updateMessageDetails({ active: checked });
                                    }}
                                />
                            }
                            label={<Typography variant="h6">{title}</Typography>}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">{description}</Typography>
                    </Grid>
                </Grid>
            )}

            {initialValues?.active && <AutoSendWelcomeMessageCurrentSettings messageDetails={initialValues} getAudio={getAudio} />}

            {memoizedTtsCard}

            {memoizedQuickSelectInventory}

            <Grid container spacing={1} flexGrow={0} alignItems="center">
                <Grid item xs={12}>
                    <MessageUsersFormAddMedias
                        subscriber={subscriber}
                        serviceName="autoSendWelcomeMessage"
                        includeMedia={formState.includeMedia}
                        setIncludeMedia={value => dispatch({ type: 'SET_INCLUDE_MEDIA', payload: value })}
                        vaultAlbum={formState.vaultAlbum}
                        setVaultAlbum={value => dispatch({ type: 'SET_VAULT_ALBUM', payload: value })}
                        vaultAlbumName={vaultAlbumName}
                        setVaultAlbumName={setVaultAlbumName}
                        medias={formState.medias}
                        setMedias={handleSetMedias}
                        mediasPreviews={formState.mediasPreviews}
                        setMediasPreviews={handleSetMediasPreviews}
                        thumbnails={thumbnails}
                        getThumbnail={getThumbnail}
                        getAudio={getAudio}
                        tagCreators={formState.tagCreators}
                        setTagCreators={value => dispatch({ type: 'SET_TAG_CREATORS', payload: value })}
                        taggedCreators={formState.taggedCreators}
                        setTaggedCreators={handleSetTaggedCreators}
                        taggedReleaseForms={formState.taggedReleaseForms}
                        setTaggedReleaseForms={handleSetTaggedReleaseForms}
                        session={null}
                        disabled={!messageDetails?.active}
                    />
                </Grid>
            </Grid>

            {formState.includeMedia && (
                <MessageUsersTagCreators
                    subscriber={subscriber}
                    tagCreators={formState.tagCreators}
                    setTagCreators={value => dispatch({ type: 'SET_TAG_CREATORS', payload: value })}
                    taggedCreators={formState.taggedCreators}
                    setTaggedCreators={handleSetTaggedCreators}
                    taggedReleaseForms={formState.taggedReleaseForms}
                    setTaggedReleaseForms={handleSetTaggedReleaseForms}
                    session={null}
                    disabled={!messageDetails?.active}
                />
            )}

            <MessageUsersPriceLock
                subscriber={subscriber}
                lockMessage={formState.lockMessage}
                setLockMessage={value => dispatch({ type: 'SET_LOCK_MESSAGE', payload: value })}
                lockMessagePrice={messageDetails?.lockMessagePrice || 0}
                setLockMessagePrice={lockMessagePrice => {
                    updateMessageDetails({ lockMessagePrice });
                }}
                lockedText={formState.lockedText}
                setLockedText={value => dispatch({ type: 'SET_LOCKED_TEXT', payload: value })}
                medias={formState.medias}
                thumbnails={thumbnails}
                mediasPreviews={formState.mediasPreviews}
                setMediasPreviews={handleSetMediasPreviews}
                session={null}
                disabled={!messageDetails?.active}
            />

            <MessageUsersFormEditor
                subscriber={subscriber}
                message={messageDetails?.message || ''}
                setMessage={message => {
                    updateMessageDetails({ message });
                }}
                messageHasRestrictedWords={messageHasRestrictedWords}
                messageFoundRestrictedWords={messageFoundRestrictedWords}
                checkForRestrictedWords={checkForRestrictedWords}
                usernameReplacement={formState.usernameReplacement}
                setUsernameReplacement={value => dispatch({ type: 'SET_USERNAME_REPLACEMENT', payload: value })}
                taggedCreators={formState.taggedCreators}
                taggedReleaseForms={formState.taggedReleaseForms}
                lockMessage={formState.lockMessage}
                lockMessagePrice={messageDetails?.lockMessagePrice || 0}
                lockedText={formState.lockedText}
                saveTemplate={saveTemplate}
                setSaveTemplate={setSaveTemplate}
                saveTemplateName={saveTemplateName}
                setSaveTemplateName={setSaveTemplateName}
                borderless={false}
                session={null}
                theme={theme}
                disabled={!messageDetails?.active}
            />

            {showAddToList && (
                <Paper variant={'outlined'} elevation={0} sx={{ mb: 1 }}>
                    <CardContent>
                        <Grid container spacing={1} flexGrow={0} alignItems="center">
                            <Grid item xs>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="medium"
                                            checked={formState.excludeUserInLists}
                                            onChange={(e, checked) => {
                                                dispatch({ type: 'SET_EXCLUDE_USER_IN_LISTS', payload: checked });
                                            }}
                                        />
                                    }
                                    label={<Typography variant="h6">Don't Message Users in Lists?</Typography>}
                                    disabled={!messageDetails?.active}
                                />
                                <Typography variant="body2">Users in these selected lists will not receive the welcome message.</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <OnlyFansListsMultiSelector
                                    targetLists={formState.excludeLists || []}
                                    setTargetLists={handleSetExcludeLists}
                                    disabled={!messageDetails?.active || !formState.excludeUserInLists}
                                    listItemBackgroundColor={alpha(theme.palette.error.main, 0.3)}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Paper>
            )}
            {showAddToList && (
                <Paper variant={'outlined'} elevation={0}>
                    <CardContent>
                        <Grid container spacing={1} flexGrow={0} alignItems="center">
                            <Grid item xs>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="medium"
                                            checked={formState.addToList}
                                            onChange={(e, checked) => {
                                                dispatch({ type: 'SET_ADD_TO_LIST', payload: checked });
                                            }}
                                        />
                                    }
                                    label={<Typography variant="h6">Add To List?</Typography>}
                                    disabled={!messageDetails?.active}
                                />
                                <Typography variant="body2">Add the user to list after sending the welcome message.</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <OnlyFansListsSelector
                                    size="small"
                                    includeBuiltInLists={false}
                                    showCreateListButton={true}
                                    targetList={formState.addToListIdAndName}
                                    setTargetList={handleSetAddToListIdAndName}
                                    disabled={!messageDetails?.active || !formState.addToList}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Paper>
            )}
        </FormContainer>
    );
};

export default AutoSendWelcomeMessageDetailsForm;
